import React, { useState, useContext, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import 'pure-react-carousel/dist/react-carousel.es.css';
import LabTab from './LabTab';
import { useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import { GlobalContext } from '../context/GlobalState';
import { languages } from 'constants';
import ScrollToTop from './ScrollToTop';
import ProductViewGridItemImage from './ProductViewGridItemImage';
import ProductViewGridItemIntro from './ProductViewGridItemIntro';
import ProductViewGridItemAction from './ProductViewGridItemAction';
import LinkedProducts from './LinkedProducts';
import { strings } from 'localisation';
import Box from '@mui/material/Box';
import PropTypes from 'prop-types';
import { ThemeProvider } from '@mui/material/styles';
import { GENERIC_SANS, withGenericSans } from 'theme';
import fs from 'fs';

// The top section and the description read in the original site's system font. The
// ThemeProvider covers MUI's typography variants; the `fontFamily` on the wrapper
// covers everything that inherits instead (ListItem, TabPanel, bare Box text).
const OriginalFont = ({ children }) => (
  <ThemeProvider theme={withGenericSans}>
    <Box sx={{ fontFamily: GENERIC_SANS }}>{children}</Box>
  </ThemeProvider>
);

OriginalFont.propTypes = { children: PropTypes.node };

export default function ProductView() {
  const location = useLocation();
  const { lang } = useContext(GlobalContext);
  const [additionalProductData, setAdditionalProductData] = useState(null);
  const [productData, setProductData] = useState(null);
  const [priceRangeData, setPriceRangeData] = useState(null);
  const [description, setDescription] = useState([]);
  const params = useParams();

  useEffect(() => {
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      window.fbq('track', 'ViewContent');
    } else {
      console.warn('fbq not available in ViewContent');
    }
  }, []);

  const [variationPrice, setVariationPrice] = useState(null);

  const fetchAddionalProductData = async () => {
    try {
      const { data: response } = await axios.get(`/singleProduct/?lang=bg`, {
        params
      });
      return response[0];
    } catch (error) {
      let errorMessage = {
        error:
          '--------------------------------------------------------------------------' +
          new Date() +
          '-  Error while trying to fetchAdditionalProductData!!!',
        error_log: error
      };
      fs.appendFile(
        'error_logs.json',
        JSON.stringify(errorMessage) +
          '--------------------------------------------------------------------------------------------------------------'
      );
    }
  };

  const fetchAdditioanlData = async () => {
    const data = await fetchAddionalProductData();
    // maybe there is a proper way to do this
    if (data != 'error') {
      setAdditionalProductData(data);
    }
  };

  const fetchMainProductData = async () => {
    try {
      const { data: response } = await axios.get(`/products/?lang=bg`, {
        params
      });

      return response[0];
    } catch (error) {
      if (error.response) {
        // Not in the 200 response range
        return 'error';
      } else {
        return 'error';
      }
    }
  };

  const fetchMainData = async () => {
    const data = await fetchMainProductData();
    // maybe there is a proper way to do this
    if (data != 'error') {
      setProductData(data);
    }
  };

  const fetchPriceRange = async (productId) => {
    try {
      params['productId'] = productId;
      const { data: response } = await axios.get(`/price/`, {
        params
      });

      let priceRange = [];
      let minPrice = null;
      let maxPrice = null;

      if (response && response.length > 1) {
        minPrice = Math.min.apply(
          null,
          response.map((obj) => parseFloat(obj.price_eur))
        );
        priceRange.push(minPrice.toFixed(2));

        maxPrice = Math.max.apply(
          null,
          response.map((obj) => parseFloat(obj.price_eur))
        );
        priceRange.push(maxPrice.toFixed(2));
        return priceRange;
      } else if (response.length === 1) {
        minPrice = parseFloat(response[0].price_eur);
        priceRange.push(minPrice.toFixed(2));
        return priceRange;
      } else {
        return priceRange;
      }
    } catch (error) {
      return []; // Return empty array on error instead of undefined
    }
  };

  const getPriceRange = async (productId) => {
    const priceRangeData = await fetchPriceRange(productId);
    setPriceRangeData(priceRangeData);
  };

  React.useEffect(() => {
    // Reset state when product ID changes
    const currentProductId = params['id'];

    // Clear previous product data when navigating to a new product
    setProductData(null);
    setAdditionalProductData(null);
    setPriceRangeData(null);
    setDescription([]);
    setVariationPrice(null);

    fetchAdditioanlData();
    if (location?.state !== null && location?.state?.Product?.id === parseInt(currentProductId)) {
      // Only use state if it matches the current product ID
      if (location?.state?.Product !== null) {
        setProductData(location.state.Product);
      }
      if (location?.state?.priceRange !== null) {
        setPriceRangeData(location.state.priceRange);
      }
    } else {
      // Always fetch fresh data from API based on URL params
      fetchMainData();
      if (currentProductId) {
        getPriceRange(currentProductId);
      }
    }
  }, [params.id, location.pathname]);

  React.useEffect(() => {
    if (
      additionalProductData &&
      languages.bg === lang &&
      'description_bg' in additionalProductData &&
      typeof additionalProductData.description_bg !== 'undefined' &&
      additionalProductData.description_bg != null
    ) {
      let description_result = additionalProductData.description_bg.split(';').map(function (item) {
        return item.trim();
      });
      setDescription(description_result);
    }
  }, [additionalProductData]);

  return (
    <ScrollToTop>
      <Container maxWidth="xl" style={{ backgroundColor: '', position: 'static', paddingTop: 10 }}>
        {/* Top section - original font. */}
        <OriginalFont>
          {/* The three columns (image 4 / intro 5 / action 3) sit flush without a gutter. `spacing`
              works by negative margins, which overflow the Container once the gutter exceeds its
              padding - 24px from sm up, 16px below - so the gutter only applies where the columns are
              actually side by side. Below sm they stack and need none. */}
          <Grid container spacing={{ xs: 0, sm: 3 }}>
            {/* Helment is already included in ProductViewGridItemIntro so if Helment is definded here it will get overwitten by Helmet in child component */}
            {productData && <ProductViewGridItemImage product={productData} />}
            {productData && (
              <ProductViewGridItemIntro
                product={productData}
                priceRange={priceRangeData}
                additionalProductData={additionalProductData}
                variationPrice={variationPrice}
              />
            )}
            {additionalProductData && productData && (
              <ProductViewGridItemAction
                product={productData}
                additionalProductData={additionalProductData}
                variationPrice={variationPrice}
                setVariationPrice={setVariationPrice}
              />
            )}
          </Grid>
        </OriginalFont>

        {/* The related-products rail deliberately keeps the modern Aurum type. */}
        {productData && productData.code && (
          <LinkedProducts productCode={productData.code} excludeId={productData.id} />
        )}

        {/* Description - original font. */}
        <OriginalFont>
          <LabTab items={description} label={strings[lang].description} />
        </OriginalFont>
      </Container>
    </ScrollToTop>
  );
}
